import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar, faChevronDown, faEyeSlash, faFilePdf, faFilter, faMoneyBill,
    faRefresh, faSave, faStore, faUniversity, faWallet
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../module_mechanics/Styles/Form.css';
import '../module_mechanics/Styles/FormList.css';
import '../module_mechanics/Styles/mechanics_cash_ledger.css';

const API = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_account.php';
const today = () => new Date().toISOString().slice(0, 10);
const money = (value) => `Rs. ${Number(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyEntry = () => ({
    payment_type: 'cash',
    account_id: '',
    direction: 'in',
    amount: '',
    transaction_date: today(),
    reference_no: '',
    description: ''
});

const PDF_ORIENTATION_OPTIONS = [
    { value: 'landscape', label: 'Landscape' },
    { value: 'portrait', label: 'Portrait' }
];
const PDF_PAGE_SIZE_OPTIONS = [
    { value: 'a4', label: 'A4' },
    { value: 'a5', label: 'A5' },
    { value: 'letter', label: 'Letter' },
    { value: 'legal', label: 'Legal' }
];
const PAYMENT_TYPE_OPTIONS = [
    { value: 'cash', label: 'Cash' },
    { value: 'digital_wallet', label: 'Digital wallet' },
    { value: 'bank_account', label: 'Bank account' }
];
const DIRECTION_OPTIONS = [
    { value: 'in', label: 'Money In' },
    { value: 'out', label: 'Money Out' }
];

function ProductCustomDropdown({ id, label, icon, value, onChange, options, disabled = false, emptyText = 'No options found', className = '' }) {
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const selected = options.find((option) => String(option.value) === String(value ?? ''));
    const filteredOptions = options.filter((option) =>
        `${option.label || ''} ${option.subLabel || ''}`.toLowerCase().includes(query.trim().toLowerCase())
    );

    useEffect(() => {
        if (!open) setQuery(selected?.label || '');
    }, [open, selected?.label]);

    useEffect(() => {
        const closeOutside = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, []);

    const choose = (option) => {
        onChange(option.value);
        setQuery(option.label);
        setOpen(false);
        setActiveIndex(0);
    };

    const handleKeyDown = (event) => {
        if (disabled) return;
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            const movement = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((current) => Math.max(0, Math.min(filteredOptions.length - 1, current + movement)));
            return;
        }
        if (event.key === 'Enter' && open && filteredOptions[activeIndex]) {
            event.preventDefault();
            choose(filteredOptions[activeIndex]);
        }
    };

    return (
        <div className={`ozui-field ozcash-custom-dropdown ${className}`} ref={rootRef}>
            <div className="ozui-input-wrapper ozui-category-wrapper">
                <input
                    type="text"
                    id={id}
                    value={open ? query : (selected?.label || '')}
                    onChange={(event) => { setQuery(event.target.value); setOpen(true); setActiveIndex(0); }}
                    onFocus={() => { setQuery(selected?.label || ''); setOpen(true); setActiveIndex(0); }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${id}_options`}
                />
                <label htmlFor={id}><FontAwesomeIcon icon={icon} />{label}</label>
                <FontAwesomeIcon icon={faChevronDown} className={`ozui-dropdown-arrow ${open ? 'ozcash-dropdown-open' : ''}`} onClick={() => !disabled && setOpen((current) => !current)} />
            </div>
            {open && !disabled && (
                <div className="ozui-category-dropdown" id={`${id}_options`} role="listbox">
                    {filteredOptions.length ? filteredOptions.map((option, index) => (
                        <div
                            key={String(option.value)}
                            className={`ozui-category-option ${String(value ?? '') === String(option.value) ? 'ozui-selected' : ''} ${index === activeIndex ? 'ozcash-option-active' : ''}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => choose(option)}
                            role="option"
                            aria-selected={String(value ?? '') === String(option.value)}
                        >
                            <div className="ozcash-option-account">
                                <div>{option.label}</div>
                                {option.subLabel && <small>{option.subLabel}</small>}
                            </div>
                        </div>
                    )) : <div className="ozui-category-empty">{emptyText}</div>}
                </div>
            )}
        </div>
    );
}

async function post(payload) {
    const response = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Ledger request failed');
    return data.data || [];
}

export default function MechanicsCashLedger() {
    const { user, currentShop } = useAuth();
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [summary, setSummary] = useState([]);
    const [rows, setRows] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [entry, setEntry] = useState(emptyEntry);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const base = useMemo(() => ({ shop_id: currentShop?.shop_id, username: user?.username }), [currentShop?.shop_id, user?.username]);
    const matchingAccounts = useMemo(
        () => accounts.filter((account) => account.account_type === entry.payment_type && Number(account.is_active) === 1),
        [accounts, entry.payment_type]
    );
    const matchingAccountOptions = useMemo(() => matchingAccounts.map((account) => ({
        value: String(account.account_id),
        label: `${account.provider_name} — ${account.account_title}`,
        subLabel: account.account_number || ''
    })), [matchingAccounts]);

    const getAccountDisplay = useCallback((item = {}) => {
        const accountId = String(item.account_id ?? '').trim();
        const account = accounts.find(candidate =>
            String(candidate.account_id ?? '').trim() === accountId
        );
        if (item.payment_type === 'cash' || (!accountId && !item.account_name)) {
            return { heading: 'Cash', number: '', pdfText: 'Cash' };
        }

        const heading = account
            ? [account.provider_name, account.account_title].filter(Boolean).join(' — ')
            : (item.account_name || 'Account');
        const pdfHeading = account
            ? [account.provider_name, account.account_title].filter(Boolean).join(' - ')
            : (item.account_name || 'Account');
        const number = String(account?.account_number || item.account_number || '').trim();
        return {
            heading,
            number,
            pdfText: [pdfHeading, number].filter(Boolean).join('\n')
        };
    }, [accounts]);

    const load = useCallback(async () => {
        if (!base.shop_id || !base.username) return;
        setLoading(true); setError('');
        try {
            const [summaryRows, ledgerRows, accountRows] = await Promise.all([
                post({ action: 'get_cash_summary', ...base, date_from: from, date_to: to }),
                post({ action: 'get_cash_ledger', ...base, date_from: from, date_to: to, limit: 500 }),
                // Include inactive accounts so historical ledger rows retain
                // their original account title and account/mobile number.
                post({ action: 'list_accounts', ...base, active_only: false })
            ]);
            setSummary(summaryRows);
            setRows(ledgerRows);
            setAccounts(accountRows);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }, [base, from, to]);

    useEffect(() => { load(); }, [load]);

    const updateEntry = (field, value) => {
        setEntry((current) => ({
            ...current,
            [field]: value,
            ...(field === 'payment_type' ? { account_id: '' } : {})
        }));
        setError('');
        setSuccess('');
    };

    const submitEntry = async (event) => {
        event.preventDefault();
        if (!base.shop_id || !base.username) return;
        const amount = Number(entry.amount);
        if (!Number.isFinite(amount) || amount <= 0) {
            setError('Enter an amount greater than zero.');
            return;
        }
        if (entry.payment_type !== 'cash' && !entry.account_id) {
            setError('Select the wallet or bank account.');
            return;
        }

        setSaving(true); setError(''); setSuccess('');
        try {
            const result = await post({
                action: 'add_manual_entry',
                ...base,
                ...entry,
                account_id: entry.payment_type === 'cash' ? null : Number(entry.account_id),
                amount
            });
            setEntry(emptyEntry());
            setSuccess(`Manual entry posted. Reference: ${result.reference_no}`);
            await load();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    const exportLedgerPDF = useCallback(() => {
        if (!rows.length) {
            setError('No ledger entries are available for the selected date range.');
            return;
        }

        setPdfLoading(true);
        setError('');
        setSuccess('');

        try {
            const pageFormat = { a4: 'a4', a5: 'a5', letter: 'letter', legal: 'legal' }[pdfPageSize] || 'a4';
            const doc = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const primary = (() => {
                const configured = localStorage.getItem('ourzest_theme') ||
                    getComputedStyle(document.documentElement).getPropertyValue('--ozui-primary') || '#6366f1';
                const hex = String(configured).trim().replace('#', '');
                const normalized = hex.length === 3
                    ? hex.split('').map(char => char + char).join('')
                    : hex.padEnd(6, '0').slice(0, 6);
                return [0, 2, 4].map(index => parseInt(normalized.slice(index, index + 2), 16) || 0);
            })();
            // Preserve line breaks so account/mobile numbers print beneath the
            // provider and account title in both PDF tables.
            const safeText = (value) => String(value ?? '').replace(/[^\x20-\x7E\n]/g, ' ');
            const pdfMoney = (value) => `Rs. ${Number(value || 0).toLocaleString('en-PK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
            const reportTitle = 'CASH & ACCOUNTS LEDGER';
            const shopName = safeText(currentShop?.shop_name || user?.business_name || 'OurZest');
            const generatedDate = new Date();
            const generatedAt = generatedDate.toLocaleString('en-PK');
            const reportId = `${today().replace(/-/g, '')}-${String(generatedDate.getTime()).slice(-6)}`;
            const generatedBy = safeText(user?.fullname || user?.username || 'System');
            const activeFilters = `Date range: ${safeText(from)} to ${safeText(to)}`;
            const margin = 12;
            const reportTableY = 51;

            // The summary endpoint currently returns only net_amount. Build the
            // Money In/Out columns from the same ledger rows printed below so
            // every account's movements are represented in the PDF.
            const accountKey = (item) => {
                const accountId = Number(item?.account_id || 0);
                if (accountId > 0) return `account:${accountId}`;
                return `${item?.payment_type || 'cash'}:${item?.account_name || 'Cash'}`.toLowerCase();
            };
            const reportAccounts = new Map();

            summary.forEach((item) => {
                reportAccounts.set(accountKey(item), {
                    account_id: item.account_id || null,
                    account_name: item.account_name || 'Cash',
                    payment_type: item.payment_type || 'cash',
                    money_in: 0,
                    money_out: 0,
                    net_amount: Number(item.net_amount || 0)
                });
            });

            rows.forEach((row) => {
                // Reversed/cancelled entries must not affect the posted balance.
                if (row.status && String(row.status).toLowerCase() !== 'posted') return;
                const key = accountKey(row);
                const current = reportAccounts.get(key) || {
                    account_id: row.account_id || null,
                    account_name: row.account_name || 'Cash',
                    payment_type: row.payment_type || 'cash',
                    money_in: 0,
                    money_out: 0,
                    net_amount: 0
                };
                const amount = Number(row.amount || 0);
                if (String(row.direction).toLowerCase() === 'out') current.money_out += amount;
                else current.money_in += amount;
                current.net_amount = current.money_in - current.money_out;
                reportAccounts.set(key, current);
            });

            const accountReportRows = Array.from(reportAccounts.values());
            const reportTotalIn = accountReportRows.reduce((total, item) => total + item.money_in, 0);
            const reportTotalOut = accountReportRows.reduce((total, item) => total + item.money_out, 0);

            const drawPageFrame = (pageNumber, totalPages) => {
                doc.setTextColor(15, 23, 42);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(17);
                doc.text(reportTitle, pageWidth / 2, 12, { align: 'center' });
                doc.setDrawColor(...primary);
                doc.setLineWidth(.8);
                doc.line(pageWidth / 2 - 12, 17, pageWidth / 2 + 12, 17);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(100, 116, 139);
                doc.text(shopName, pageWidth / 2, 23, { align: 'center' });
                doc.setFontSize(7);
                doc.text(`Total entries: ${rows.length}  |  Generated: ${safeText(generatedAt)}`, pageWidth / 2, 28, { align: 'center' });
                doc.setDrawColor(229, 231, 235);
                doc.setLineWidth(.2);
                doc.line(margin, 32, pageWidth - margin, 32);
                doc.line(margin, 37, pageWidth - margin, 37);
                doc.setTextColor(75, 85, 99);
                doc.setFontSize(6.5);
                doc.text(`Report ID: #${reportId}`, margin, 35);
                doc.text(`Generated by: ${generatedBy}`, pageWidth / 2, 35, { align: 'center' });
                doc.text(`Shop Code: ${safeText(currentShop?.shop_code || 'N/A')}`, pageWidth - margin, 35, { align: 'right' });
                doc.setFillColor(249, 250, 251);
                doc.rect(margin, 40, pageWidth - margin * 2, 7, 'F');
                doc.setDrawColor(...primary);
                doc.setLineWidth(.8);
                doc.line(margin, 40, margin, 47);
                doc.setTextColor(75, 85, 99);
                doc.setFontSize(6.5);
                doc.text(`FILTERS APPLIED: ${activeFilters}`, margin + 3, 44.5, { maxWidth: pageWidth - margin * 2 - 6 });
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(.2);
                doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
                doc.setFontSize(7);
                doc.setTextColor(100, 116, 139);
                doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
                doc.text('Generated by OurZest', pageWidth - margin, pageHeight - 6, { align: 'right' });
            };

            drawPageFrame(1, 1);

            autoTable(doc, {
                startY: reportTableY,
                margin: { top: reportTableY, left: margin, right: margin, bottom: 15 },
                head: [['Account', 'Type', 'Money In', 'Money Out', 'Net Amount']],
                body: accountReportRows.length ? [
                    ...accountReportRows.map((item) => [
                    safeText(getAccountDisplay(item).pdfText),
                    safeText(String(item.payment_type || 'cash').replace(/_/g, ' ')),
                    pdfMoney(item.money_in),
                    pdfMoney(item.money_out),
                    pdfMoney(item.net_amount)
                    ]),
                    ['TOTAL', '', pdfMoney(reportTotalIn), pdfMoney(reportTotalOut), pdfMoney(reportTotalIn - reportTotalOut)]
                ] : [['All accounts', '-', pdfMoney(0), pdfMoney(0), pdfMoney(0)]],
                theme: 'grid',
                headStyles: {
                    fillColor: primary,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: pdfOrientation === 'landscape' ? 7.5 : 6.4,
                    cellPadding: 2
                },
                bodyStyles: {
                    textColor: [51, 65, 85],
                    fontSize: pdfOrientation === 'landscape' ? 7.2 : 6.2,
                    cellPadding: 1.7,
                    lineColor: [226, 232, 240],
                    lineWidth: .15
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right', fontStyle: 'bold' }
                },
                didParseCell: (cellData) => {
                    if (cellData.section === 'body' && cellData.row.index === accountReportRows.length) {
                        cellData.cell.styles.fillColor = [241, 245, 249];
                        cellData.cell.styles.textColor = [15, 23, 42];
                        cellData.cell.styles.fontStyle = 'bold';
                    }
                }
            });

            const transactionStartY = (doc.lastAutoTable?.finalY || 24) + 8;
            const usableWidth = pageWidth - margin * 2;
            autoTable(doc, {
                startY: transactionStartY,
                margin: { top: reportTableY, right: margin, bottom: 15, left: margin },
                head: [['Date', 'Source', 'Account', 'Direction', 'Amount', 'Reference', 'Status']],
                body: rows.map((row) => [
                    safeText(row.transaction_date),
                    safeText(row.source_label || row.description || row.source_type),
                    safeText(getAccountDisplay(row).pdfText),
                    safeText(row.direction === 'in' ? 'Money In' : 'Money Out'),
                    pdfMoney(row.amount),
                    safeText(row.display_reference || row.reference_no || `CASH-${row.cash_id}`),
                    safeText(row.status)
                ]),
                theme: 'grid',
                showHead: 'everyPage',
                headStyles: {
                    fillColor: [30, 41, 59],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: pdfOrientation === 'landscape' ? 7.2 : 6.3,
                    cellPadding: 2
                },
                bodyStyles: {
                    textColor: [51, 65, 85],
                    fontSize: pdfOrientation === 'landscape' ? 6.8 : 5.8,
                    cellPadding: 1.7,
                    lineColor: [226, 232, 240],
                    lineWidth: .15,
                    overflow: 'linebreak'
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { cellWidth: usableWidth * .10 },
                    1: { cellWidth: usableWidth * .22 },
                    2: { cellWidth: usableWidth * .18 },
                    3: { cellWidth: usableWidth * .10, halign: 'center' },
                    4: { cellWidth: usableWidth * .12, halign: 'right', fontStyle: 'bold' },
                    5: { cellWidth: usableWidth * .18 },
                    6: { cellWidth: usableWidth * .10, halign: 'center' }
                }
            });

            const totalPages = doc.internal.getNumberOfPages();
            for (let page = 1; page <= totalPages; page += 1) {
                doc.setPage(page);
                drawPageFrame(page, totalPages);
            }

            const safeShop = (currentShop?.shop_name || 'Ledger').replace(/[^a-z0-9_-]/gi, '_');
            doc.save(`Cash_Ledger_${safeShop}_${from}_to_${to}.pdf`);
            setSuccess(`PDF generated with ${rows.length} ledger entries.`);
        } catch (pdfError) {
            console.error('Ledger PDF error:', pdfError);
            setError(pdfError.message || 'Could not generate the ledger PDF.');
        } finally {
            setPdfLoading(false);
        }
    }, [rows, summary, from, to, currentShop, user, pdfOrientation, pdfPageSize, getAccountDisplay]);

    return (
        <div className="ozui-container ozlistui-container ozcash-page">
            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation" aria-hidden="true">
                    <div className="gradient-orb orb-1" />
                    <div className="gradient-orb orb-2" />
                    <div className="gradient-orb orb-3" />
                    <div className="gradient-orb orb-4" />
                    <div className="gradient-orb orb-5" />
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>Cash & Accounts Ledger</h1>
                            <p>Sales, payments, advances, salaries, expenses and account movements.</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                       
                        <button className="ozlistui-header-filter-btn" type="button" onClick={() => setShowFilters(current => !current)}>
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? 'Hide Filters' : 'Filters'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {showFilters && (
                <section className="ozlistui-filters-section" aria-label="Ledger filters and report controls">
                    <div className="ozlistui-filters-row">
                        <div className="ozlistui-filter-group">
                            <div className="ozlistui-floating-wrapper">
                                <input id="ledger_from" type="date" value={from} max={to || today()} onChange={(event) => setFrom(event.target.value)} placeholder=" " />
                                <label htmlFor="ledger_from"><FontAwesomeIcon icon={faCalendar} />Start Date</label>
                            </div>
                        </div>
                        <div className="ozlistui-filter-group">
                            <div className="ozlistui-floating-wrapper">
                                <input id="ledger_to" type="date" value={to} min={from} max={today()} onChange={(event) => setTo(event.target.value)} placeholder=" " />
                                <label htmlFor="ledger_to"><FontAwesomeIcon icon={faCalendar} />End Date</label>
                            </div>
                        </div>
                        <div className="ozlistui-filter-group ozcash-filter-dropdown">
                            <ProductCustomDropdown id="ledger_pdf_orientation" label="Orientation" icon={faFilePdf} value={pdfOrientation} onChange={setPdfOrientation} options={PDF_ORIENTATION_OPTIONS} />
                        </div>
                        <div className="ozlistui-filter-group ozcash-filter-dropdown">
                            <ProductCustomDropdown id="ledger_pdf_size" label="Page Size" icon={faFilePdf} value={pdfPageSize} onChange={setPdfPageSize} options={PDF_PAGE_SIZE_OPTIONS} />
                        </div>
                        <div className="ozlistui-filter-group ozcash-filter-action">
                            <button className="ozlistui-refresh-btn" type="button" onClick={load} disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} spin={loading} />
                                <span className="ozlistui-btn-text">{loading ? 'Loading…' : 'Refresh'}</span>
                            </button>
                        </div>
                        <div className="ozlistui-filter-group ozcash-filter-action">
                            <button className="ozlistui-refresh-btn ozcash-pdf-btn" type="button" onClick={exportLedgerPDF} disabled={loading || pdfLoading || rows.length === 0}>
                                <FontAwesomeIcon icon={faFilePdf} />
                                <span className="ozlistui-btn-text">{pdfLoading ? 'Generating…' : 'Export PDF'}</span>
                            </button>
                        </div>
                    </div>
                </section>
            )}

            <form className="ozui-form ozcash-entry" onSubmit={submitEntry}>
                
                <div className="ozui-form-row ozcash-entry-grid">
                    <ProductCustomDropdown id="cash_payment_type" label="Type" icon={faWallet} value={entry.payment_type} onChange={(value) => updateEntry('payment_type', value)} options={PAYMENT_TYPE_OPTIONS} disabled={saving} />
                    {entry.payment_type !== 'cash' && <ProductCustomDropdown id="cash_account" label="Account" icon={faUniversity} value={entry.account_id} onChange={(value) => updateEntry('account_id', value)} options={matchingAccountOptions} disabled={saving} emptyText={`No active ${entry.payment_type === 'digital_wallet' ? 'digital-wallet' : 'bank'} account found`} />}
                    <ProductCustomDropdown id="cash_direction" label="Direction" icon={faMoneyBill} value={entry.direction} onChange={(value) => updateEntry('direction', value)} options={DIRECTION_OPTIONS} disabled={saving} />
                    <div className="ozui-field"><div className="ozui-input-wrapper">
                        <input id="cash_amount" type="number" min="0.01" step="0.01" value={entry.amount} onChange={(event) => updateEntry('amount', event.target.value)} placeholder=" " disabled={saving} required />
                        <label htmlFor="cash_amount"><FontAwesomeIcon icon={faMoneyBill} />Amount</label>
                    </div></div>
                    <div className="ozui-field"><div className="ozui-input-wrapper">
                        <input id="cash_date" type="date" value={entry.transaction_date} onChange={(event) => updateEntry('transaction_date', event.target.value)} placeholder=" " disabled={saving} required />
                        <label htmlFor="cash_date"><FontAwesomeIcon icon={faCalendar} />Date</label>
                    </div></div>
                    <div className="ozui-field"><div className="ozui-input-wrapper">
                        <input id="cash_reference" type="text" maxLength="100" value={entry.reference_no} onChange={(event) => updateEntry('reference_no', event.target.value)} placeholder=" " disabled={saving} />
                        <label htmlFor="cash_reference"><FontAwesomeIcon icon={faFilePdf} />Reference (optional)</label>
                    </div></div>
                    <div className={`ozui-field ozcash-notes ${entry.payment_type === 'cash' ? 'ozcash-notes-wide' : ''}`}><div className="ozui-input-wrapper">
                        <input id="cash_notes" type="text" maxLength="500" value={entry.description} onChange={(event) => updateEntry('description', event.target.value)} placeholder=" " disabled={saving} />
                        <label htmlFor="cash_notes"><FontAwesomeIcon icon={faMoneyBill} />Reason or details</label>
                    </div></div>
                    <div className="ozui-field ozcash-entry-submit">
                        <button className="ozui-btn ozui-btn-primary" type="submit" disabled={saving || loading}><FontAwesomeIcon icon={faSave} /><span>{saving ? 'Posting…' : 'Save'}</span></button>
                    </div>
                </div>
                {entry.payment_type !== 'cash' && matchingAccounts.length === 0 && <p className="ozcash-hint" role="status">No active {entry.payment_type === 'digital_wallet' ? 'digital-wallet' : 'bank'} account is available. Add or activate one first.</p>}
            </form>
            {error && <div className="ozcash-error" role="alert">{error}</div>}
            {success && <div className="ozcash-success" role="status">{success}</div>}
            <div className="ozcash-cards">
                {summary.map((item) => {
                    const accountDisplay = getAccountDisplay(item);
                    return <article key={`${item.payment_type}-${item.account_id || 0}`}>
                        <div className="ozcash-account-heading"><small>{accountDisplay.heading}</small></div>
                        {accountDisplay.number && <div className="ozcash-account-number"><small>{accountDisplay.number}</small></div>}
                        <strong>{money(item.net_amount)}</strong>
                        <span>{item.payment_type.replace('_', ' ')}</span>
                    </article>;
                })}
            </div>
            <section className="ozcash-table-card">
                {loading ? <p className="ozcash-loading" role="status">Loading ledger…</p> : (
                    <div className="ozcash-scroll">
                        <table>
                            <thead><tr><th>Date</th><th>Source</th><th>Account</th><th>Direction</th><th>Amount</th><th>Reference</th><th>Status</th></tr></thead>
                            <tbody>
                        {rows.map((row) => {
                            const accountDisplay = getAccountDisplay(row);
                            return <tr key={row.cash_id}>
                            <td data-label="Date">{row.transaction_date}</td>
                            <td data-label="Source"><div className="ozcash-source"><span>{row.source_label || row.description || row.source_type}</span>{row.transaction_type && <small>{row.transaction_type.replace(/_/g, ' ')}</small>}</div></td>
                            <td data-label="Account">
                                <div className="ozcash-account-display">
                                    <div>{accountDisplay.heading}</div>
                                    {accountDisplay.number && <small>{accountDisplay.number}</small>}
                                </div>
                            </td>
                            <td data-label="Direction"><span className={`ozcash-direction ${row.direction}`}>{row.direction === 'in' ? 'In' : 'Out'}</span></td>
                            <td data-label="Amount" className="ozcash-amount">{money(row.amount)}</td>
                            <td data-label="Reference" className="ozcash-reference">{row.display_reference || row.reference_no || `CASH-${row.cash_id}`}</td>
                            <td data-label="Status"><span className={`ozcash-status ${row.status}`}>{row.status}</span></td>
                        </tr>;
                        })}
                        {rows.length === 0 && <tr><td className="ozcash-empty" colSpan="7">No entries found for this date range.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
